import { Component } from '@theme/component';
import { getStoreReviewSummary, formatReviewAverage } from '@theme/judgeme-review-data';

/**
 * @typedef {Object} ReviewSummaryRefs
 * @property {HTMLElement} [rating]
 * @property {HTMLElement} [count]
 * @property {HTMLElement[]} [starFills]
 * @property {HTMLTemplateElement} [productBadge]
 */

/** @extends {Component<ReviewSummaryRefs>} */
class ProductReviewSummary extends Component {
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    void this.#loadSummary();
  }

  updatedCallback() {
    super.updatedCallback();
    void this.#loadSummary();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
    this.#request = undefined;
  }

  async #loadSummary() {
    this.#request?.abort();
    this.#request = undefined;
    if (this.dataset.reviewMode !== 'dynamic' || !this.dataset.productId) return;

    // Judge.me's rendered badge also includes grouped product reviews. Keep it
    // inert in a template so it supplies data without mounting a second widget.
    const badge = this.refs.productBadge?.content.querySelector('[data-number-of-reviews][data-average-rating]');
    const countValue = badge?.getAttribute('data-number-of-reviews') ?? this.dataset.productCount;
    const ratingValue = badge?.getAttribute('data-average-rating') ?? this.dataset.productRating;
    const count = Number(countValue);
    const average = Number(ratingValue);
    if (countValue && ratingValue && Number.isInteger(count) && count > 0 && average > 0 && average <= 5) {
      this.#renderSummary({ count, average });
      return;
    }

    // Do not replace a known positive product count with unrelated store stats
    // if its average is temporarily unavailable.
    this.dataset.summaryPending = 'true';
    if (this.refs.count) this.refs.count.textContent = this.dataset.countLabel || '';
    if (count > 0) return;

    const request = new AbortController();
    this.#request = request;
    try {
      const summary = await getStoreReviewSummary({
        shopDomain: this.dataset.shopDomain || '',
        signal: request.signal,
      });
      if (!request.signal.aborted && this.isConnected && this.#request === request) this.#renderSummary(summary);
    } catch {
      // Keep the summary hidden if Judge.me cannot supply store totals.
      // Never fall back to the old manually entered rating or review count.
    } finally {
      if (this.#request === request) this.#request = undefined;
    }
  }

  /** @param {{count: number, average: number}} summary */
  #renderSummary({ count, average }) {
    if (this.refs.rating) {
      this.refs.rating.textContent = `${Number(formatReviewAverage(average))}${this.dataset.ratingSuffix || ''}`;
    }
    if (this.refs.count) {
      const plus = this.dataset.showPlus === 'true' && count > Number(this.dataset.plusThreshold) ? '+' : '';
      this.refs.count.textContent = `${count.toLocaleString()}${plus} ${this.dataset.countLabel || ''}`.trim();
    }
    for (const [index, star] of (this.refs.starFills || []).entries()) {
      star.style.width = `${Math.max(0, Math.min(100, (average - index) * 100))}%`;
    }
    this.dataset.summaryPending = 'false';
  }

  /** Smoothly follows the native reviews anchor for pointer and keyboard activation.
   * @param {MouseEvent} event
   */
  scrollToReviews(event) {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const widget = [...document.querySelectorAll('#judgeme_product_reviews')].find(
      (element) => element instanceof HTMLElement && element.dataset.productId === this.dataset.productId
    );
    if (!(widget instanceof HTMLElement)) return;

    event.preventDefault();
    const activeTab = widget.querySelector('[role="tab"][aria-selected="true"]');
    if (activeTab instanceof HTMLButtonElement && !activeTab.disabled) activeTab.focus({ preventScroll: true });
    else {
      if (!widget.hasAttribute('tabindex')) widget.setAttribute('tabindex', '-1');
      widget.focus({ preventScroll: true });
    }
    widget.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      block: 'start',
    });
  }
}

if (!customElements.get('product-review-summary')) customElements.define('product-review-summary', ProductReviewSummary);
